"use client"

import type React from "react"

import { useEffect, useState } from "react"
import type { ClauseType } from "features/contracts/CreateContract"
import type { AxiosResponse } from "axios"
import { getMyContractAPI } from "api/apiHelper"
import Backdrop from "components/Backdrop"
import { Link } from "react-router-dom"
import { usePermissions } from "app/hooks"
// import type { EmployeeType } from "types/EmployeeType"

export interface ContractType {
  id: string
  clauses_in_contract: ClauseType[]
  employee: EmployeeType["id"]
}

const MyContract: React.FC = () => {
  const [contract, setContract] = useState<ContractType | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const permissions = usePermissions() || []

  const getMyContract = async () => {
    try {
      const response: AxiosResponse<ContractType> = await getMyContractAPI()
      console.log("Contract Data: ", response.data)
      setContract(response.data || null)
    } catch (error: any) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getMyContract()
  }, [])

  if (isLoading) {
    return <Backdrop show={isLoading} />
  }

  return (
    <div className="container mt-5">
      <div>
        {/* Header */}
        <div className="flex items-center justify-end mb-6">
          {permissions && permissions.includes("contracts.add_contract") && (
            <Link
              to="/my-contract/create-contract"
              className="flex items-center px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500"
            >
              <i className="fas fa-plus mr-2"></i>
              Create Contract
            </Link>
          )}
        </div>

        {/* Main Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {contract ? (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Contract Clauses</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your employment contract contains {contract.clauses_in_contract.length} clauses
                </p>
              </div>

              {/* Contract Clauses Table */}
              <div className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          No.
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Legal Text
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                      {contract.clauses_in_contract.map((clause, index) => (
                        <tr
                          key={clause.id}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                            index % 2 === 0 ? "bg-white dark:bg-gray-800" : "bg-gray-50/50 dark:bg-gray-700/25"
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{clause.legal_text}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            /* Empty State */
            <div className="text-center py-16">
              <div className="max-w-sm mx-auto">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-file-contract text-2xl text-gray-400"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Contract Found</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">You don't have an employment contract yet.</p>
                {permissions && permissions.includes("contracts.add_contract") && (
                  <Link
                    to="/my-contract/create-contract"
                    className="inline-flex items-center px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 transition-colors"
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Create Contract
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MyContract
